import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  if (user && user !== false) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Selamat datang, ${res.user.name.split(" ")[0]}`);
      navigate("/", { replace: true });
    } else {
      setErrorMsg(res.error || "Gagal masuk");
    }
  }

  return (
    <div className="min-h-screen grid-backdrop flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left / Hero (desktop) */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-teal-900 text-white">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(45,212,191,0.35), transparent 40%), radial-gradient(circle at 80% 80%, rgba(20,184,166,0.25), transparent 45%)",
          }}
        />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <Logo variant="white" />
          <div className="max-w-md staggered-fade">
            <div className="text-[11px] uppercase tracking-[0.32em] font-bold text-teal-200">
              PT Sucofindo EPISI
            </div>
            <h1 className="mt-3 font-display text-5xl font-extrabold leading-[1.05]">
              ATLAS.
              <br />
              <span className="text-teal-200">Satu Platform</span> <br /> Satu Ekosistem.
            </h1>
            <p className="mt-6 text-teal-100/80 leading-relaxed max-w-sm">
              Platform Digital 
              <br />
              — Insan PT SUCOFINDO EPISI.
            </p>
          </div>
          <div className="flex items-center gap-2 text-teal-200/80 text-xs">
            <ShieldCheck size={14} /> Developed & Maintained by @agung_gumilar
          </div>
        </div>
      </div>

      {/* Right / Form */}
      <div className="flex-1 flex flex-col items-stretch justify-center px-6 py-10 sm:px-12">
        <div className="lg:hidden mb-8">
          <Logo />
        </div>
        <div className="max-w-md w-full mx-auto lg:mx-0">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-teal-700">
            Masuk
          </div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">
            Selamat datang kembali
          </h2>
          <p className="mt-3 text-slate-500 text-sm">
            Gunakan akun Anda untuk mengakses ATLAS.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700 text-xs font-bold uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                placeholder="nama@episi.co.id"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 bg-white border-slate-200 focus-visible:ring-teal-600 focus-visible:ring-offset-1"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 text-xs font-bold uppercase tracking-wider">
                Password
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-10 bg-white border-slate-200 focus-visible:ring-teal-600 focus-visible:ring-offset-1"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                  onClick={() => setShowPwd((v) => !v)}
                  data-testid="login-toggle-pwd"
                  aria-label="Toggle password"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {errorMsg ? (
              <div
                className="text-sm rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2"
                data-testid="login-error"
              >
                {errorMsg}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-teal-800 hover:bg-teal-900 text-white font-semibold gap-2 group"
              data-testid="login-submit-btn"
            >
              {loading ? "Memproses…" : (
                <>
                  Masuk <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>

          
        </div>
      </div>
    </div>
  );
}
